import {
  defineNuxtRouteMiddleware,
  useNuxtApp,
  useRequestEvent,
  navigateTo,
  createError,
  useError,
  showError,
} from 'nuxt/app';
import globalMiddlewareHeaders from '~~/common/server/globalMiddlewareHeaders.js';
import { extractResourceFromPath } from '~~/common/utils/urlResource.js';
import { getSeoModuleFromUrlResource } from '~~/common/utils/seoModule.js';
import { SEO_MODULE_CMS, SEO_MODULE_GLOBAL } from '~~/common/config/SeoConfig.ts';
import { useNotificationStore } from '~~/common/stores/notification';
import { useSeoStore } from '~~/common/stores/seo';
import { useContentsStore } from '~~/common/stores/contents';
import { useUserStore } from '~~/common/stores/user';
import { useUserInterfaceStore } from '~~/common/stores/userInterface';
import { COOKIE_TOKEN_EXPIRED, handleRedirectAfterTokenExpired } from '~~/common/server/globalMiddlewareContans.js';
import { useUrlResourceStore } from '~~/common/stores/urlResource.js';
import { useConfigStore } from '~~/common/stores/config.js';
import { CAREER_PAGES_TURNED_OFF } from '~~/common/config/CareerTurnedOffConfig';

export default defineNuxtRouteMiddleware(async (to) => {
  const {
    $awt,
    $logger,
    $cookies,
    $pinia,
    $i18n,
    $router,
    $config,
  } = useNuxtApp();
  const res = useRequestEvent()?.node?.res || {};

  const notificationStore = useNotificationStore($pinia);
  const userStore = useUserStore($pinia);
  const seoStore = useSeoStore($pinia);
  const contentsStore = useContentsStore($pinia);
  const urlResourceStore = useUrlResourceStore($pinia);
  const userInterfaceStore = useUserInterfaceStore($pinia);
  const configStore = useConfigStore($pinia);
  const isCareerTurnOffApiControlEnabled = $config.public.isCareerTurnOffApiControlEnabled;
  const careerPageShutdown = configStore.configCommon?.persistentConfig?.career_page_shutdown;
  if (isCareerTurnOffApiControlEnabled && careerPageShutdown && to.path.startsWith('/karrier')) {
    const error = {
      statusCode: 422,
      statusText: CAREER_PAGES_TURNED_OFF,
      fatal: true,
    };
    showError(error);
    throw createError(error);
  }

  // NOTE: to.meta.layoutTransition has to be disabled for the error page.
  // It caused a layout duplication error when navigating to the error page
  // on the client side.
  if (useError().value) {
    to.meta.layoutTransition = false;
  }

  if (typeof res?.setHeader === 'function') {
    if (typeof globalMiddlewareHeaders === 'object') {
      for (const headerKey in globalMiddlewareHeaders) {
        if (globalMiddlewareHeaders[headerKey] === null) {
          res.removeHeader(headerKey);
        } else {
          res.setHeader(headerKey, globalMiddlewareHeaders[headerKey]);
        }
      }
    }
  }

  if (userStore.forceLogoutToRedirect) {
    handleRedirectAfterTokenExpired($i18n, $cookies, navigateTo);
  } else if ($cookies.get(COOKIE_TOKEN_EXPIRED)) {
    $cookies.remove(COOKIE_TOKEN_EXPIRED);
    notificationStore.pushError({
      text: {
        title: $awt('aw.token_expired'),
      },
    });
  }
  let is404PageRequested = false;
  try {
    const langlessPath = to.path.replace(
      new RegExp(
        `^(${$i18n.localeCodes.value.map(lc => '/' + lc).join('|')})($|/)`,
      ),
      '/',
    );
    const cmsPage = [
      {
        regex: /^\/$/,
        staticContentType: 'dh_homepage',
      },
      {
        regex: /^\/shop(\/)*$/,
        staticContentType: 'homepage',
      },
      {
        regex: /^\/admin\/cms\/content_preview\/([a-zA-Z0-9_-]+)$/,
        staticContentType: '',
      },
    ].find(cmsPage => cmsPage.regex.test(langlessPath));
    if (cmsPage) {
      const contentPreviewHash = langlessPath.match(cmsPage.regex)[1];
      try {
        const STATIC_CONTENT_TYPE = cmsPage.staticContentType;
        if (STATIC_CONTENT_TYPE) {
          await contentsStore.fetchStaticContent({ type: STATIC_CONTENT_TYPE });
          if (contentsStore.staticContent[STATIC_CONTENT_TYPE]?.id) {
            await seoStore.fetchSeoData({
              seoModule: SEO_MODULE_CMS,
              id: contentsStore.staticContent[STATIC_CONTENT_TYPE].id,
            });
          }
        } else if (contentPreviewHash) {
          try {
            await contentsStore.fetchContentPreview({ hash: contentPreviewHash });
            await seoStore.fetchSeoData({
              seoModule: SEO_MODULE_CMS,
              id: contentsStore.previewData.id,
            });
          } catch (err) {
            if (err?.response?.status === 404) {
              is404PageRequested = true;
            } else {
              $logger.error(err);
            }
          }
        }
      } catch (error) {
        if (error.response?.status !== 404) {
          $logger.error(error);
        }
      }
    } else {
      const urlResource = extractResourceFromPath(to.fullPath);
      const seoModule = getSeoModuleFromUrlResource(urlResource, urlResourceStore, to);
      await Promise.allSettled([
        seoStore.fetchSeoData(seoModule),
        (async () => {
          if (
            seoModule.seoModule === SEO_MODULE_CMS
              && seoModule.id
              && urlResourceStore?.data?.seoResourceId === seoModule.id
          ) {
            await contentsStore.fetchContent({ pageId: urlResourceStore.data.resourceId });
            userInterfaceStore.setBackground({ ...contentsStore.data });
          } else if (seoModule.seoModule === SEO_MODULE_GLOBAL &&
              seoModule.id === -1 &&
              urlResourceStore?.data?.seoResourceId &&
              urlResourceStore.data.resourceId &&
              langlessPath === `/shop/${urlResourceStore?.data.slug}`
          ) {
            $router.replace(`/${urlResourceStore?.data.slug}`);
          }
        })(),
      ]);
    }
  } catch (err) {
    $logger.error(err);
  }
  if (is404PageRequested) {
    throw createError({
      statusCode: 404,
      fatal: true,
    });
  }
});
